'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LazyImage } from './lazy-image';
import { cn } from '../utils/cn';

export interface BannerCarouselItem {
  id: string;
  imageUrl: string;
  title?: string | null;
  subtitle?: string | null;
  badge?: string | null;
  linkType?: string | null;
  linkValue?: string | null;
}

interface BannerCarouselProps {
  banners?: BannerCarouselItem[];
  onBannerClick: (linkType: string | null, linkValue: string | null) => void;
}

const readyBannerSignatureCache = new Set<string>();

export function BannerCarousel({
  banners = [],
  onBannerClick,
}: BannerCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [, setCurrentIndex] = useState(0);
  const [initialBannersReady, setInitialBannersReady] = useState(false);
  const [failedBannerIds, setFailedBannerIds] = useState<Record<string, true>>({});
  const isTouchingRef = useRef(false);
  const autoScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settledBannerIdsRef = useRef<Set<string>>(new Set());

  const bannerSignature = useMemo(
    () => banners.map((banner) => `${banner.id}:${banner.imageUrl}`).join('|'),
    [banners],
  );
  const initiallyVisibleCount = Math.min(2, banners.length);

  const scrollToIndex = useCallback((index: number) => {
    const container = scrollRef.current;
    const child = container?.children[index] as HTMLElement | undefined;
    if (!container || !child) return;
    container.scrollTo({
      left: child.offsetLeft - 16,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (banners.length <= 1 || !initialBannersReady) return;
    const interval = setInterval(() => {
      if (isTouchingRef.current) return;
      setCurrentIndex((previous) => {
        const next = (previous + 1) % banners.length;
        scrollToIndex(next);
        return next;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [banners.length, scrollToIndex, initialBannersReady]);

  const handleTouchStart = () => {
    isTouchingRef.current = true;
    if (autoScrollTimerRef.current) clearTimeout(autoScrollTimerRef.current);
  };

  const handleTouchEnd = () => {
    autoScrollTimerRef.current = setTimeout(() => {
      isTouchingRef.current = false;
    }, 5000);
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    const firstCard = container?.children[0] as HTMLElement | undefined;
    if (!container || !firstCard) return;
    const cardWidth = firstCard.clientWidth;
    const gap = 8;
    const index = Math.round(container.scrollLeft / (cardWidth + gap));
    setCurrentIndex(index);
  };

  const markBannerSettled = useCallback((bannerId: string, index: number, failed = false) => {
    if (settledBannerIdsRef.current.has(bannerId)) return;

    settledBannerIdsRef.current.add(bannerId);

    if (failed) {
      setFailedBannerIds((previous) =>
        previous[bannerId] ? previous : { ...previous, [bannerId]: true },
      );
    }

    if (index >= initiallyVisibleCount) return;

    const initialIds = banners.slice(0, initiallyVisibleCount).map((banner) => banner.id);
    const allInitialBannersSettled = initialIds.every((id) =>
      settledBannerIdsRef.current.has(id),
    );

    if (allInitialBannersSettled) {
      if (bannerSignature) readyBannerSignatureCache.add(bannerSignature);
      setInitialBannersReady(true);
    }
  }, [bannerSignature, banners, initiallyVisibleCount]);

  useEffect(() => {
    settledBannerIdsRef.current = new Set();
    setFailedBannerIds({});

    if (banners.length === 0) {
      setInitialBannersReady(false);
      return;
    }

    if (bannerSignature && readyBannerSignatureCache.has(bannerSignature)) {
      setInitialBannersReady(true);
      return;
    }

    setInitialBannersReady(false);

    const timer = window.setTimeout(() => {
      setInitialBannersReady(true);
    }, 2500);

    return () => window.clearTimeout(timer);
  }, [bannerSignature, banners.length]);

  if (banners.length === 0) {
    return (
      <div className="flex gap-2 overflow-hidden px-4 pb-2">
        <div className="h-40 w-[calc(100%-3.5rem)] max-w-[320px] flex-shrink-0 animate-pulse rounded-[20px_0_20px_0] bg-card" />
        <div className="h-40 w-[calc(100%-3.5rem)] max-w-[320px] flex-shrink-0 animate-pulse rounded-[20px_0_20px_0] bg-card" />
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <div className="relative">
        {!initialBannersReady && (
          <div className="pointer-events-none absolute inset-0 z-10 flex gap-2 overflow-hidden px-4 pb-2">
            <div className="h-40 w-[calc(100%-3.5rem)] max-w-[320px] flex-shrink-0 animate-pulse rounded-[20px_0_20px_0] bg-card" />
            <div className="h-40 w-[calc(100%-3.5rem)] max-w-[320px] flex-shrink-0 animate-pulse rounded-[20px_0_20px_0] bg-card" />
          </div>
        )}
        <div
          className={cn(
            'transition-opacity duration-500',
            initialBannersReady ? 'opacity-100' : 'opacity-0',
          )}
        >
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-2 pr-4 scrollbar-hide"
            style={{ scrollPaddingInline: '16px' }}
          >
            {banners.map((banner, index) => (
              <div
                key={banner.id}
                className={cn(
                  'relative h-40 w-[calc(100%-3.5rem)] max-w-[320px] flex-shrink-0 snap-start overflow-hidden rounded-[20px_0_20px_0] bg-card',
                  banner.linkType &&
                    banner.linkValue &&
                    'cursor-pointer transition-transform active:scale-[0.98]',
                )}
                onClick={() => onBannerClick(banner.linkType ?? null, banner.linkValue ?? null)}
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_42%),linear-gradient(180deg,#303237_0%,#1c1d20_100%)]" />
                {!failedBannerIds[banner.id] && (
                  <LazyImage
                    src={banner.imageUrl}
                    alt={banner.title || ''}
                    loading={index < initiallyVisibleCount ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : undefined}
                    className="absolute inset-0 h-full w-full object-cover"
                    onLoad={() => markBannerSettled(banner.id, index)}
                    onError={() => markBannerSettled(banner.id, index, true)}
                  />
                )}
                {banner.badge && (
                  <div className="absolute right-0 top-0 rounded-bl-[10px] bg-black px-3 py-1 text-sm text-white">
                    {banner.badge}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 flex w-full items-end justify-between gap-2 p-2.5">
                  <div>
                    {banner.title && (
                      <h3 className="font-sans text-[13px] font-bold leading-tight text-white">
                        {banner.title}
                      </h3>
                    )}
                    <p className="mt-0.5 font-sans text-[10px] text-[#8d8d8d]">
                      {banner.subtitle}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
